import { useState } from 'react'
import MarkerValue from './MarkerValue'
import InterpretabilityBadge from './InterpretabilityBadge'
import MarkerContextSheet from './MarkerContextSheet'

const getStatusBadgeClass = (status) => {
  const statusMap = {
    optimal: 'badge-success',
    normal: 'badge-success',
    borderline: 'badge-warning',
    slightly_abnormal: 'badge-warning',
    abnormal: 'badge-danger',
    critical: 'badge-danger',
    unknown: 'badge-gray',
  }
  return statusMap[status] || 'badge-gray'
}

const getStatusLabel = (status) => {
  const labelMap = {
    optimal: 'Optimal',
    normal: 'Normal',
    borderline: 'Borderline',
    slightly_abnormal: 'Slightly Abnormal',
    abnormal: 'Abnormal',
    critical: 'Critical',
    unknown: 'Unknown',
  }
  return labelMap[status] || status
}

const getCategoryLabel = (category) => {
  if (!category) return 'Other'

  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function MarkersByCategoryView({ results, bloodTestId = null }) {
  const [expandedCategories, setExpandedCategories] = useState(new Set())
  const [contextMarker, setContextMarker] = useState(null)

  if (!results || results.length === 0) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-gray-500 text-center">No marker data available</p>
        </div>
      </div>
    )
  }

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    const category = result.marker_category || 'other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(result)
    return acc
  }, {})

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(category)) {
      newExpanded.delete(category)
    } else {
      newExpanded.add(category)
    }
    setExpandedCategories(newExpanded)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl font-semibold">Blood Markers by Category</h2>
      </div>
      <div className="card-body">
        <div className="space-y-4">
          {Object.entries(groupedResults).map(([category, markers]) => {
            const isExpanded = expandedCategories.has(category)
            const abnormalCount = markers.filter(m => m.is_abnormal).length
            const criticalCount = markers.filter(m => m.is_critical).length

            return (
              <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center">
                    <svg
                      className={`h-5 w-5 text-gray-500 mr-2 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-gray-900">
                      {getCategoryLabel(category)}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({markers.length} markers)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {criticalCount > 0 && (
                      <span className="badge badge-danger">{criticalCount} critical</span>
                    )}
                    {abnormalCount > 0 && (
                      <span className="badge badge-warning">{abnormalCount} abnormal</span>
                    )}
                  </div>
                </button>

                {/* Category Content */}
                {isExpanded && (
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Marker
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Value
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Reference Range
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-surface-container divide-y divide-gray-200">
                          {markers.map((marker, idx) => (
                            <tr
                              key={idx}
                              className={marker.is_abnormal ? 'bg-red-50' : marker.is_critical ? 'bg-danger-50' : ''}
                            >
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                <div className="flex flex-col items-start gap-1">
                                  <span>{marker.marker_name || 'Unknown'}</span>
                                  {bloodTestId && (
                                    <InterpretabilityBadge
                                      interpretability={marker.interpretability}
                                      onClick={() => setContextMarker(marker)}
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <MarkerValue result={marker} />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {marker.reference_range || 'Not specified'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`badge ${getStatusBadgeClass(marker.status)}`}>
                                  {getStatusLabel(marker.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      {bloodTestId && (
        <MarkerContextSheet
          bloodTestId={bloodTestId}
          resultId={contextMarker?.id}
          markerName={contextMarker?.marker_name}
          open={Boolean(contextMarker)}
          onClose={() => setContextMarker(null)}
        />
      )}
    </div>
  )
}
